import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CiSquarePlus } from "react-icons/ci";
import { FaHeart } from "react-icons/fa";
import { FaMoneyBill1Wave } from "react-icons/fa6";
import { IoChevronDown } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { achievementList } from "../data/achievement/achievementData.ts";
import { coursesData } from "../data/courses/courseData.ts";
import {
  formatHeartRecoveryTime,
  getHeartRecoveryInfo,
  getSelectedLanguage,
  getUserData,
  getXpProgress,
  purchaseHearts,
} from "../utils/userStorage.ts";
import AchievementProgressBar from "./achievement/AchievementProgressBar.tsx";
import StageCircle from "./achievement/StageCircle.tsx";
import { ChestClosed, ChestOpen } from "./icons/ChestIcons.tsx";
import { Instagram, Linkedin } from "lucide-react";

interface RightSidebarProps {
  darkMode?: boolean;
  profileOnly?: boolean;
}

const RightSidebar: React.FC<RightSidebarProps> = ({
  darkMode = false,
  profileOnly = false,
}) => {
  // Tooltip hover state for heart
  const [heartTooltipVisible, setHeartTooltipVisible] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const heartTooltipRef = React.useRef<HTMLSpanElement>(null);
  // Ref for tooltip hide timeout (for desktop hover delay)
  const tooltipHideTimeout = React.useRef<number | null>(null);

  // Close dropdown on sidebar scroll (desktop only)
  useEffect(() => {
    if (!dropdownOpen) return;
    // Adjust selector to match your sidebar container
    const sidebar = document.querySelector(".md\\:block.w-80");
    if (!sidebar) return;
    const handleScroll = () => setDropdownOpen(false);
    sidebar.addEventListener("scroll", handleScroll);
    return () => {
      sidebar.removeEventListener("scroll", handleScroll);
    };
  }, [dropdownOpen]);

  // Toggle tooltip on click
  const handleHeartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setHeartTooltipVisible((v) => !v);
  };

  // Prevent scrolling when heart tooltip or language dropdown is open (only for mobile top bar)
  useEffect(() => {
    if (!profileOnly) return;
    if (heartTooltipVisible || dropdownOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [heartTooltipVisible, dropdownOpen, profileOnly]);

  // Close tooltip on outside click (heart)
  useEffect(() => {
    if (!heartTooltipVisible) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        heartTooltipRef.current &&
        !heartTooltipRef.current.contains(event.target as Node)
      ) {
        setHeartTooltipVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [heartTooltipVisible]);

  // Close language dropdown on outside click (mobile top bar only)
  const langDropdownRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!profileOnly || !dropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen, profileOnly]);

  // Format currency with dot as thousand separator, max 5 digits, truncate if more
  const formatCurrency = (value: number) => {
    const str = value.toLocaleString("id-ID");
    // Remove non-digit chars for length check
    const digitsOnly = str.replace(/\D/g, "");
    if (digitsOnly.length > 5) {
      // Truncate and add ellipsis
      // Show first 5 digits, then ...
      // But keep formatting
      let shown = digitsOnly.slice(0, 5);
      // Format shown with dot
      let formatted = Number(shown).toLocaleString("id-ID");
      return formatted + "...";
    }
    return str;
  };
  const navigate = useNavigate();

  // For each achievement group, track current stage index and progress
  const [currentStages, setCurrentStages] = useState(
    achievementList.map(() => 0)
  );
  const [progress, setProgress] = useState(achievementList.map((group) => [0]));
  const [rewarded, setRewarded] = useState(achievementList.map(() => false));

  // Real user data state
  const [userData, setUserData] = useState(() => getUserData());
  const [selectedLanguageLabel, setSelectedLanguageLabel] = useState(() => {
    const savedLanguage = getSelectedLanguage();
    return savedLanguage || activeLanguages[0]?.label || "";
  });

  // Heart recovery state
  const [heartRecoveryInfo, setHeartRecoveryInfo] = useState(() =>
    getHeartRecoveryInfo()
  );
  const [timeRemaining, setTimeRemaining] = useState(
    heartRecoveryInfo.timeUntilNextHeart
  );

  // Update heart recovery info every second
  useEffect(() => {
    const updateHeartRecovery = () => {
      const info = getHeartRecoveryInfo();
      setHeartRecoveryInfo(info);
      setTimeRemaining(info.timeUntilNextHeart);
    };

    // Update immediately
    updateHeartRecovery();

    // Set up interval to update every second when hearts are recovering
    const interval = setInterval(updateHeartRecovery, 1000);

    return () => clearInterval(interval);
  }, [userData?.meta?.heart]); // Re-run when heart count changes

  // Update user data when localStorage changes
  useEffect(() => {
    const handleUserDataChange = () => {
      const newUserData = getUserData();
      setUserData(newUserData);

      const savedLanguage = getSelectedLanguage();
      if (savedLanguage && savedLanguage !== selectedLanguageLabel) {
        setSelectedLanguageLabel(savedLanguage);
      }
    };

    // Listen for custom userDataChanged event
    window.addEventListener("userDataChanged", handleUserDataChange);

    // Also listen for storage events (for cross-tab updates)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "galaxy_aksara_user_data") {
        handleUserDataChange();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("userDataChanged", handleUserDataChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [selectedLanguageLabel]);

  // Get real user values or fallback to defaults
  const currency = userData?.meta?.money || 0;
  const heart = userData?.meta?.heart ?? 5; // Use nullish coalescing to allow 0

  // Get dynamic XP and level data
  const xpData = getXpProgress();
  const userLevel = xpData.level;
  const currentXP = xpData.currentXp;
  const xpToNextLevel = xpData.xpToNextLevel;
  const xpProgressPercent = xpData.progressPercent;

  // Only show languages with active === true
  const activeLanguages = coursesData.filter((lang) => lang.active);

  const selectedLanguage = activeLanguages.find(
    (lang) => lang.label === selectedLanguageLabel
  ) ||
    activeLanguages[0] || { label: "" };

  // Handler for chest click: move to next stage if available
  const handleChestClick = (idx: number) => {
    const groupIdx = idx;
    const stageIdx = currentStages[groupIdx];
    const group = achievementList[groupIdx];
    if (stageIdx < group.length - 1) {
      // Move to next stage
      setCurrentStages((prev) => {
        const next = [...prev];
        next[groupIdx] += 1;
        return next;
      });
      setRewarded((prev) => {
        const next = [...prev];
        next[groupIdx] = false;
        return next;
      });
      setProgress((prev) => {
        const next = prev.map((arr) => [...arr]);
        // Ensure next stage progress is initialized
        if (!next[groupIdx][stageIdx + 1]) next[groupIdx][stageIdx + 1] = 0;
        return next;
      });
    } else {
      // Last stage, just mark as rewarded
      setRewarded((prev) => {
        const next = [...prev];
        next[groupIdx] = true;
        return next;
      });
    }
  };

  // Only show the first stage of each achievement group (but update as user progress)
  const uniqueAchievements = achievementList.map(
    (group, idx) => group[currentStages[idx]]
  );
  const stageCounts = achievementList.map((group) => group.length);

  // Show only the top 3 achievements
  const topAchievements = uniqueAchievements.slice(0, 3);

  const handleLanguageChange = (lang: any) => {
    setSelectedLanguageLabel(lang.label);
    setDropdownOpen(false);
    // TODO: Update selected language in user storage
  };

  // Responsive: hide sidebar on mobile, show as sidebar on md+ screens
  // If profileOnly, show only profile section (for mobile top bar)
  if (profileOnly) {
    return (
      <div
        className={`w-full p-2 ${
          darkMode ? "bg-gray-800" : "bg-white"
        } shadow-sm`}
      >
        {/* Profile Section Only (mobile top bar) */}
        <div className="flex flex-row items-center justify-between relative gap-2">
          {/* Language Dropdown */}
          <div className="w-full" ref={langDropdownRef}>
            <button
              className={`flex text-start items-center font-medium focus:outline-none w-full ${
                darkMode ? "text-gray-200" : "text-gray-600"
              } bg-transparent py-3`}
              style={{
                fontFamily: "Feather Bold",
                minWidth: 120,
                maxWidth: 150,
              }}
              onClick={() => setDropdownOpen((open) => !open)}
              type="button"
            >
              <span
                className="truncate"
                style={{
                  width: 150,
                  fontSize: "15px",
                  lineHeight: "20px",
                  display: "block",
                }}
                title={selectedLanguage.label}
              >
                {selectedLanguage.label}
              </span>
              <IoChevronDown className="ml-1 flex-shrink-0" />
            </button>
            {dropdownOpen && (
              <>
                {/* Overlay */}
                <div
                  className="fixed left-0 right-0 top-[64px] bottom-0 z-30 bg-black bg-opacity-30"
                  style={{ pointerEvents: "auto" }}
                  onClick={() => setDropdownOpen(false)}
                />
                <div
                  className={`fixed left-0 right-0 top-[64px] w-screen max-w-none shadow-lg z-40 ${
                    darkMode
                      ? "bg-gray-800 border-t border-gray-700"
                      : "bg-white border-t border-gray-200"
                  }`}
                  style={{ borderRadius: 0 }}
                >
                  <div className="px-4 pt-3">
                    <div
                      className={`text-sm font-base tracking-wide ${
                        darkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                      style={{ fontFamily: "Feather Bold" }}
                    >
                      Kursusku
                    </div>
                    <div
                      className={`${
                        darkMode
                          ? "border-b border-gray-700"
                          : "border-b border-gray-300"
                      } mt-2`}
                    />
                  </div>
                  {/* Scrollable language list */}
                  <div
                    className="max-h-48 overflow-y-auto py-2"
                    style={{ fontFamily: "DIN Next Rounded" }}
                  >
                    {activeLanguages.map((lang) => (
                      <button
                        key={lang.label}
                        className={`flex items-center w-full px-3 py-2 text-sm transition
                        ${
                          lang.label === selectedLanguage.label
                            ? darkMode
                              ? "bg-blue-900/30 text-blue-300 font-bold"
                              : "bg-blue-50 text-blue-600 font-bold"
                            : darkMode
                            ? "hover:bg-gray-700/50 text-gray-300"
                            : "hover:bg-gray-100 text-gray-700"
                        }`}
                        onClick={() => {
                          handleLanguageChange(lang);
                        }}
                      >
                        <span
                          className="text-left whitespace-nowrap overflow-hidden"
                          style={{ textOverflow: "clip" }}
                        >
                          {lang.label}
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="pb-3">
                    <div
                      className={`${
                        darkMode
                          ? "border-b border-gray-700"
                          : "border-b border-gray-300"
                      } mx-4 mb-2`}
                    />
                    <button
                      className={`flex text-start items-center px-4 py-1 w-full mt-2 text-sm font-base transition ${
                        darkMode
                          ? "text-gray-300 hover:bg-gray-700/50"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                      style={{ fontFamily: "Feather Bold" }}
                      onClick={() => {
                        navigate("/courses");
                      }}
                    >
                      <CiSquarePlus className="mr-2 text-3xl" />
                      Tambah Bahasa Baru
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          {/* Currency and Heart Centered */}
          <div
            className="flex justify-center items-center gap-6 mt-2 mb-1"
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            <span className="flex items-center text-green-500">
              <FaMoneyBill1Wave className="mr-1 size-5" />
              <span
                className="font-semibold truncate"
                style={{
                  minWidth: 47,
                  maxWidth: 60,
                  display: "inline-block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "center",
                }}
              >
                {formatCurrency(currency)}
              </span>
            </span>
            <span className="flex items-center text-red-500 relative">
              <span
                className="relative cursor-pointer flex items-center"
                ref={heartTooltipRef}
                onClick={handleHeartClick}
              >
                <FaHeart className="mr-1" />
                <span className="font-semibold ml-1">{heart}</span>
                {/* Overlay and Tooltip: stays open until click outside */}
                {heartTooltipVisible && (
                  <>
                    {/* Overlay */}
                    <div
                      className="fixed left-0 right-0 top-[64px] bottom-0 z-30 bg-black bg-opacity-30"
                      style={{ pointerEvents: "auto" }}
                    />
                    {/* Tooltip */}
                    <span
                      className={`fixed left-0 right-0 top-[64px] w-screen max-w-none z-40 px-4 py-4 border-t shadow-lg transition ${
                        darkMode
                          ? "bg-gray-800 border-t border-gray-700 text-gray-100"
                          : "bg-white border-t border-gray-200 text-gray-800"
                      } opacity-100 pointer-events-auto`}
                      style={{ borderRadius: 0 }}
                    >
                      <span className="flex justify-center mb-1">
                        {[...Array(5)].map((_, i) => (
                          <FaHeart
                            key={i}
                            className={`mx-1 ${
                              i < heart
                                ? "text-red-500"
                                : darkMode
                                ? "text-gray-700"
                                : "text-gray-300"
                            }`}
                            style={{ fontSize: "1rem" }}
                          />
                        ))}
                      </span>
                      <div className="mb-2 text-center">
                        {heart === 5 ? (
                          <div
                            className={`text-sm font-semibold ${
                              darkMode ? "text-gray-100" : "text-gray-800"
                            }`}
                            style={{ fontFamily: "DIN Next Rounded" }}
                          >
                            Hatimu masih penuh
                            <br />
                            Ayo terus belajar!
                          </div>
                        ) : (
                          <span
                            className={`text-sm font-semibold ${
                              darkMode ? "text-gray-100" : "text-gray-800"
                            }`}
                            style={{ fontFamily: "DIN Next Rounded" }}
                          >
                            Hati selanjutnya dalam{" "}
                            <span className="text-red-500">
                              {formatHeartRecoveryTime(timeRemaining)}
                            </span>
                          </span>
                        )}
                      </div>
                      <button
                        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border transition text-xs font-semibold mt-1${
                          heart === 5 || (userData?.meta?.money || 0) < 50
                            ? " opacity-50 cursor-not-allowed"
                            : darkMode
                            ? " bg-gray-700 border-gray-600 hover:bg-gray-600"
                            : " bg-white border-gray-300 hover:bg-gray-200"
                        }`}
                        style={{ fontFamily: "DIN Next Rounded", minWidth: 0 }}
                        type="button"
                        tabIndex={-1}
                        disabled={
                          heart === 5 || (userData?.meta?.money || 0) < 50
                        }
                        onClick={() => {
                          const result = purchaseHearts(1);
                          if (result.success) {
                            window.dispatchEvent(new Event("userDataChanged"));
                          } else {
                            alert(result.message);
                          }
                        }}
                      >
                        <span className="flex items-center">
                          <FaHeart className="mr-1 text-red-500" /> Isi Ulang
                          Hati
                        </span>
                        <span className="flex items-center text-green-500">
                          <FaMoneyBill1Wave className="mr-1" />
                          50
                        </span>
                      </button>
                    </span>
                  </>
                )}
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  }

  function showHeartTooltip(
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ): void {
    if (tooltipHideTimeout.current) {
      clearTimeout(tooltipHideTimeout.current);
      tooltipHideTimeout.current = null;
    }
    setHeartTooltipVisible(true);
  }

  function hideHeartTooltip(
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ): void {
    if (tooltipHideTimeout.current) {
      clearTimeout(tooltipHideTimeout.current);
    }
    tooltipHideTimeout.current = window.setTimeout(() => {
      setHeartTooltipVisible(false);
    }, 200); // 200ms delay
  }

  // Desktop sidebar (md+)
  return (
    <div
      className="hidden md:block w-80 p-6 space-y-6 sticky top-0 z-10 overflow-y-auto"
      style={{ height: "100vh", overflowY: "auto" }}
    >
      {/* Profile Section */}
      <div
        className={`items-center rounded-lg shadow-sm p-4 ${
          darkMode ? "bg-gray-800 border border-gray-700" : "bg-white"
        }`}
      >
        <div className="flex items-center justify-between">
          {/* Language Dropdown */}
          <div className="relative" style={{ width: 120 }}>
            <button
              className={`flex text-start items-center font-medium focus:outline-none w-full ${
                darkMode ? "text-gray-200" : "text-gray-600"
              }`}
              style={{ fontFamily: "Feather Bold", width: 120 }}
              onClick={() => setDropdownOpen((open) => !open)}
              type="button"
            >
              <span
                className="truncate"
                style={{
                  width: 120,
                  fontSize: "15px",
                  lineHeight: "20px",
                  display: "block",
                }}
                title={selectedLanguage.label}
              >
                {selectedLanguage.label}
              </span>
              <IoChevronDown className="ml-1 flex-shrink-0" />
            </button>
            {dropdownOpen && createPortal && (
              <div
                className="fixed w-[220px] rounded-lg shadow-lg z-[9999]"
                style={{
                  left: "50%",
                  top: "85px", // adjust for your header height
                  transform:
                    window.innerWidth >= 1024
                      ? "translateX(280%)" // desktop (lg+)
                      : "translateX(50%)", // tablet (md)
                  background: darkMode ? "#1f2937" : "#fff",
                  border: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
                }}
              >
                <div className="px-4 pt-3">
                  <div
                    className={`text-sm font-base tracking-wide ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                    style={{ fontFamily: "Feather Bold" }}
                  >
                    Kursusku
                  </div>
                  <div
                    className={`${
                      darkMode
                        ? "border-b border-gray-700"
                        : "border-b border-gray-300"
                    } mt-2`}
                  />
                </div>
                {/* Scrollable language list */}
                <div
                  className="max-h-48 overflow-y-auto py-2"
                  style={{ fontFamily: "DIN Next Rounded" }}
                >
                  {activeLanguages.map((lang) => (
                    <button
                      key={lang.label}
                      className={`flex items-center w-full px-3 py-2 text-sm transition
                        ${
                          lang.label === selectedLanguage.label
                            ? darkMode
                              ? "bg-blue-900/30 text-blue-300 font-bold"
                              : "bg-blue-50 text-blue-600 font-bold"
                            : darkMode
                            ? "hover:bg-gray-700/50 text-gray-300"
                            : "hover:bg-gray-100 text-gray-700"
                        }`}
                      onClick={() => {
                        handleLanguageChange(lang);
                      }}
                    >
                      <span
                        className="text-left whitespace-nowrap overflow-hidden"
                        style={{ textOverflow: "clip" }}
                      >
                        {lang.label}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="px-4 pb-3">
                  <div
                    className={`${
                      darkMode
                        ? "border-b border-gray-700"
                        : "border-b border-gray-300"
                    } mb-2`}
                  />
                  <button
                    className={`flex text-start items-center w-full mt-2 text-sm font-base transition ${
                      darkMode
                        ? "text-gray-300 hover:bg-gray-700/50"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                    style={{ fontFamily: "Feather Bold" }}
                    onClick={() => {
                      navigate("/courses");
                    }}
                  >
                    <CiSquarePlus className="mr-2 text-3xl" />
                    Tambah Bahasa Baru
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Currency, Heart */}

          <div
            className="flex items-center space-x-3 px-2 min-w-0"
            style={{ fontFamily: "DIN Next Rounded", maxWidth: 180 }}
          >
            <span className="flex items-center text-green-500">
              <FaMoneyBill1Wave className="mr-1 size-5" />
              <span
                className="font-semibold truncate"
                style={{
                  minWidth: 47,
                  maxWidth: 50,
                  display: "inline-block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {formatCurrency(currency)}
              </span>
            </span>
            <span className="flex items-center text-red-500 relative">
              <span
                className="relative cursor-pointer flex items-center"
                ref={heartTooltipRef}
                onMouseEnter={(event) => {
                  if (!profileOnly) showHeartTooltip(event);
                }}
                onMouseLeave={(event) => {
                  if (!profileOnly) hideHeartTooltip(event);
                }}
                onClick={(e) => {
                  if (profileOnly) {
                    e.stopPropagation();
                    setHeartTooltipVisible((v) => !v);
                  }
                }}
              >
                <FaHeart className="mr-1" />
                <span className="font-semibold ml-1">{heart}</span>
                {/* Tooltip on hover (desktop) or click (mobile) */}
                <span
                  className={`absolute left-auto -right-4 top-full mt-2 z-30 px-3 py-3 border rounded-lg shadow-lg transition whitespace-nowrap cursor-default ${
                    darkMode
                      ? "bg-gray-800 border-gray-700 text-gray-100"
                      : "bg-white border-gray-300 text-gray-800"
                  } ${
                    heartTooltipVisible
                      ? "opacity-100 pointer-events-auto"
                      : "opacity-0 pointer-events-none"
                  }`}
                  style={{ minWidth: 220 }}
                  onMouseEnter={(event) => {
                    if (!profileOnly) showHeartTooltip(event);
                  }}
                  onMouseLeave={(event) => {
                    if (!profileOnly) hideHeartTooltip(event);
                  }}
                >
                  <span className="flex justify-center mb-1">
                    {[...Array(5)].map((_, i) => (
                      <FaHeart
                        key={i}
                        className={`mx-1 ${
                          i < heart
                            ? "text-red-500"
                            : darkMode
                            ? "text-gray-700"
                            : "text-gray-300"
                        }`}
                        style={{ fontSize: "1rem" }}
                      />
                    ))}
                  </span>
                  <div className="mb-2 text-center">
                    {heart === 5 ? (
                      <div
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-100" : "text-gray-800"
                        }`}
                        style={{ fontFamily: "DIN Next Rounded" }}
                      >
                        Hatimu masih penuh
                        <br />
                        Ayo terus belajar!
                      </div>
                    ) : (
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-100" : "text-gray-800"
                        }`}
                        style={{ fontFamily: "DIN Next Rounded" }}
                      >
                        Hati selanjutnya dalam{" "}
                        <span className="text-red-500">
                          {formatHeartRecoveryTime(timeRemaining)}
                        </span>
                      </span>
                    )}
                  </div>
                  <button
                    className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border transition text-xs font-semibold mt-1${
                      heart === 5 || (userData?.meta?.money || 0) < 50
                        ? " opacity-50 cursor-not-allowed"
                        : darkMode
                        ? " bg-gray-700 border-gray-600 hover:bg-gray-600"
                        : " bg-white border-gray-300 hover:bg-gray-200"
                    }`}
                    style={{ fontFamily: "DIN Next Rounded", minWidth: 0 }}
                    type="button"
                    tabIndex={-1}
                    disabled={heart === 5 || (userData?.meta?.money || 0) < 50}
                    onClick={() => {
                      const result = purchaseHearts(1);
                      if (result.success) {
                        // Trigger data refresh
                        window.dispatchEvent(new Event("userDataChanged"));
                      } else {
                        alert(result.message);
                      }
                    }}
                  >
                    <span className="flex items-center">
                      <FaHeart className="mr-1 text-red-500" /> Isi Ulang Hati
                    </span>
                    <span className="flex items-center text-green-500">
                      <FaMoneyBill1Wave className="mr-1" />
                      50
                    </span>
                  </button>
                </span>
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* User Level Section */}
      <div
        className={`rounded-lg p-4 shadow-sm ${
          darkMode ? "bg-gray-800 border border-gray-700" : "bg-white"
        }`}
      >
        <h3
          className={`font-base mb-3 ${
            darkMode ? "text-gray-100" : "text-gray-800"
          }`}
          style={{ fontFamily: "Feather Bold" }}
        >
          Level
        </h3>
        {/* Ensure variables are defined above in the component */}
        <div>
          <div className="flex items-center mb-2">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-500 text-white font-base text-lg mr-3"
              style={{ fontFamily: "Feather Bold" }}
            >
              {userLevel}
            </div>
            <div>
              <div
                className={`text-xs ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
                style={{ fontFamily: "DIN Next Rounded" }}
              >
                XP: {currentXP}/{xpToNextLevel}
              </div>
              <div
                className={`w-44 rounded-full h-2 mt-1 ${
                  darkMode ? "bg-gray-700" : "bg-gray-200"
                }`}
              >
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{
                    fontFamily: "DIN Next Rounded",
                    width: `${xpProgressPercent}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
          <p
            className={`text-xs ${
              darkMode ? "text-gray-400" : "text-gray-500"
            }`}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            {xpToNextLevel - currentXP} XP lagi untuk naik ke level{" "}
            {userLevel + 1}
          </p>
        </div>
      </div>

      {/* Pencapaian Terbaru */}
      <div
        className={`rounded-lg p-4 shadow-sm ${
          darkMode ? "bg-gray-800 border border-gray-700" : "bg-white"
        }`}
      >
        <h3
          className={`font-base mb-3 ${
            darkMode ? "text-gray-100" : "text-gray-800"
          }`}
          style={{ fontFamily: "Feather Bold" }}
        >
          Pencapaian Terbaru
        </h3>
        <div className="space-y-4 max-h-[350px] overflow-y-auto">
          {topAchievements.map((ach, idx) => {
            const stageIdx = currentStages[idx];
            const isComplete = progress[idx][stageIdx] >= ach.goal;
            const isLastStage = stageIdx === stageCounts[idx] - 1;
            return (
              <div
                key={ach.achievementId}
                className="flex items-center gap-4 px-3 py-2"
              >
                {/* Stage Circle */}
                <StageCircle
                  current={stageIdx + 1}
                  total={stageCounts[idx]}
                  isGold={
                    (stageCounts[idx] === 1 && isComplete && rewarded[idx]) ||
                    (stageCounts[idx] === 2 && isComplete && rewarded[idx]) ||
                    (isLastStage && isComplete && rewarded[idx])
                  }
                  size={44}
                  fontSize={14}
                  darkMode={darkMode}
                />
                {/* Achievement Info & Progress */}
                <div className="flex-1 min-w-0">
                  <div
                    className={`font-base text-xs truncate ${
                      darkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                    style={{ fontFamily: "Feather Bold" }}
                  >
                    {ach.title}
                  </div>
                  {/* Progress Bar */}
                  <div className="flex items-center gap-2 mt-1">
                    <AchievementProgressBar
                      value={progress[idx][stageIdx] || 0}
                      max={ach.goal}
                      height={12}
                      fontSize={10}
                      darkMode={darkMode}
                    />
                    {/* Chest Button */}
                    <button
                      className={`ml-2 p-1 rounded-lg border-2 ${
                        isComplete
                          ? darkMode
                            ? "border-yellow-400 bg-yellow-500/40"
                            : "border-yellow-400 bg-yellow-200"
                          : darkMode
                          ? "border-gray-600 bg-gray-800"
                          : "border-gray-300 bg-gray-100"
                      } duration-200 transform hover:-translate-y-0.5 transition-all`}
                      style={{
                        cursor:
                          isComplete && !rewarded[idx] ? "pointer" : "default",
                      }}
                      disabled={!isComplete || rewarded[idx]}
                      onClick={() => handleChestClick(idx)}
                      aria-label="Claim Reward"
                    >
                      {isComplete ? (
                        <ChestOpen size={20} />
                      ) : (
                        <ChestClosed size={20} />
                      )}
                    </button>
                  </div>
                  {/* Demo Progress Button */}
                  {/* <button
                    className="mt-1 px-2 py-1 text-xs rounded bg-blue-100 text-blue-700"
                    onClick={() => handleDemoProgress(idx)}
                    disabled={isComplete && !isLastStage ? true : rewarded[idx]}
                  >
                    +1 Progress (Demo)
                  </button> */}
                </div>
              </div>
            );
          })}
        </div>
        <button
          className="w-full mt-4 text-center text-sm text-blue-600 hover:text-blue-700"
          style={{ fontFamily: "Feather Bold" }}
          onClick={() => navigate("/achievement")}
        >
          Lihat Semua
        </button>
      </div>
      <footer className="w-full py-2 mt-5 bg-transparent flex flex-col items-center gap-2 z-10 relative">
        <div
          className="text-gray-500 text-sm font-semibold"
          style={{ fontFamily: "DIN Next Rounded" }}
        >
          &copy; 2025 Galaxy Developer
        </div>
        <div className="flex gap-4 mt-1">
          <a
            href="https://www.instagram.com/galaxy.developer/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-gray-500 hover:text-pink-500 transition font-semibold text-sm"
          >
            <Instagram className="w-5 h-5" />
          </a>
          <a
            href="https://www.linkedin.com/company/galaxydeveloper"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-gray-500 hover:text-blue-700 transition font-semibold text-sm"
          >
            <Linkedin className="w-5 h-5" />
          </a>
        </div>
      </footer>
    </div>
  );
};

export default RightSidebar;
